export type CmsStatus = 'active' | 'inactive';
export type CmsLoginMethod = 'email_password';

export interface CmsDomain {
  readonly id: string;
  readonly schoolId: string;
  readonly domain: string;
  readonly status: CmsStatus;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface CmsSchool {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly status: CmsStatus;
  readonly domains: readonly CmsDomain[];
  readonly teacherCount?: number;
  readonly studentCount?: number;
  readonly groups?: readonly { readonly name: string; readonly studentCount: number }[];
  readonly hasCredentialExport?: boolean;
}

export interface CmsStudent {
  readonly id: string;
  readonly studentId: string;
  readonly studentName: string;
  readonly className: string;
  readonly classNumber: string;
  readonly preferredLanguage: 'zh-HK' | 'en';
  readonly status: CmsStatus;
  readonly email: string;
  readonly loginName?: string;
  readonly loginMethod: CmsLoginMethod;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface CmsTeacher {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly schoolId: string;
  readonly status: CmsStatus;
  readonly loginMethod: CmsLoginMethod;
  readonly firstLoginAt?: string;
  readonly lastLoginAt?: string;
}

export interface CmsSystemAdmin {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly status: CmsStatus;
  readonly loginMethod: CmsLoginMethod;
  readonly firstLoginAt?: string;
  readonly lastLoginAt?: string;
  readonly createdAt?: string;
  readonly updatedAt?: string;
}

export interface CmsGame {
  readonly id: string;
  readonly name: string;
  readonly version: string;
}

export interface CmsAttempt {
  readonly id: string;
  readonly sessionId: string;
  readonly gameId: string;
  readonly gameVersion: string;
  readonly score: number;
  readonly durationMs: number;
  readonly status: 'completed' | 'abandoned';
  readonly completedAt: string;
}

export interface DashboardReport {
  readonly schoolId: string;
  readonly studentCount: number;
  readonly participantCount: number;
  readonly attemptCount: number;
  readonly completedGameCount: number;
  readonly completionRate: number;
  readonly perGameParticipation: readonly {
    readonly game: CmsGame;
    readonly participantCount: number;
    readonly completedCount: number;
    readonly participationRate: number;
  }[];
}

export interface SchoolReportResult {
  readonly bestAttempt: CmsAttempt | null;
  readonly attemptCount: number;
  readonly lastPlayedAt: string | null;
}

export interface SchoolReportRow {
  readonly student: CmsStudent;
  readonly results: Readonly<Record<string, SchoolReportResult>>;
}

export interface SchoolReport {
  readonly schoolId: string;
  readonly games: readonly CmsGame[];
  readonly rows: readonly SchoolReportRow[];
  readonly averages: Readonly<
    Record<
      string,
      { readonly averageScore: number | null; readonly averageDurationMs: number | null; readonly studentCount: number }
    >
  >;
}

export interface StudentReport {
  readonly student: CmsStudent;
  readonly games: readonly {
    readonly game: CmsGame;
    readonly bestAttempt: CmsAttempt | null;
    readonly attempts: readonly CmsAttempt[];
  }[];
}

export interface LeaderboardEntry {
  readonly rank: number;
  readonly student: CmsStudent;
  readonly attempt: CmsAttempt;
}

export interface LeaderboardReport {
  readonly schoolId: string;
  readonly metric: 'highest' | 'fastest';
  readonly byGame: readonly {
    readonly game: CmsGame;
    readonly entries: readonly LeaderboardEntry[];
  }[];
}

export interface ImportRowError {
  readonly row: number;
  readonly field: string;
  readonly code: string;
  readonly message: string;
}

export interface ImportPreview {
  readonly previewToken: string;
  readonly totalRows: number;
  readonly validRows: number;
  readonly invalidRows: number;
  readonly duplicateRows: number;
  readonly errors: readonly ImportRowError[];
  readonly rows: readonly Readonly<Record<string, string>>[];
  readonly expiresAt: string;
}

export interface ImportedCredential {
  readonly accountId: string;
  readonly role: 'teacher' | 'student';
  readonly loginName: string;
  readonly temporaryPassword: string;
  readonly schoolId: string;
  readonly displayName: string;
  readonly loginMethod: CmsLoginMethod;
}

export interface StudentMutationResult {
  readonly student: CmsStudent;
  readonly credential?: ImportedCredential;
}

export interface TeacherMutationResult {
  readonly teacher: CmsTeacher;
  readonly credential?: ImportedCredential;
}

export interface ImportResult {
  readonly imported: number;
  readonly credentials: readonly ImportedCredential[];
  readonly csv: string;
}

export interface SchoolSetupInput {
  readonly name: string;
  readonly domain: string;
  readonly groupCount: number;
  readonly teacherCount: number;
  readonly studentsPerGroup: number;
  readonly preferredLanguage: 'zh-HK' | 'en';
}

export interface SchoolSetupResult {
  readonly school: CmsSchool;
  readonly domain: CmsDomain;
  readonly groups: readonly { readonly name: string; readonly studentCount: number }[];
  readonly credentials: readonly ImportedCredential[];
  readonly csv: string;
}

export interface CmsAuditEvent {
  readonly id: string;
  readonly schoolId: string | null;
  readonly actorAccountId: string;
  readonly actorRole: 'admin' | 'teacher' | 'student' | 'anonymous';
  readonly action: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly occurredAt: string;
  readonly metadata: Readonly<Record<string, string | number | boolean | null>>;
}

interface SchoolListResponse {
  readonly schools: readonly CmsSchool[];
}

interface StudentListResponse {
  readonly students: readonly CmsStudent[];
}

interface TeacherListResponse {
  readonly teachers: readonly CmsTeacher[];
}

interface SystemAdminListResponse {
  readonly admins: readonly CmsSystemAdmin[];
}

interface AuditListResponse {
  readonly events: readonly CmsAuditEvent[];
}

interface ApiFailure {
  readonly error?: string;
  readonly message?: string;
  readonly details?: unknown;
}

export interface ReportFilters {
  readonly schoolId?: string;
  readonly groupName?: string;
  readonly gameId?: string;
  readonly search?: string;
  readonly completionStatus?: 'completed' | 'not_started' | 'abandoned';
  readonly studentId?: string;
  readonly from?: string;
  readonly to?: string;
}

export class CmsClientError extends Error {
  constructor(readonly code: string, message: string, readonly details?: unknown) {
    super(message);
    this.name = 'CmsClientError';
  }
}

export class CmsClient {
  private readonly baseUrl: string;

  constructor(baseUrl = import.meta.env.VITE_CMS_API_BASE_URL ?? '') {
    this.baseUrl = baseUrl.trim().replace(/\/$/, '');
  }

  async schools(query = ''): Promise<readonly CmsSchool[]> {
    return (await this.json<SchoolListResponse>('/api/admin/schools', { query })).schools;
  }

  createSchool(input: { code: string; name: string; status?: CmsStatus }): Promise<CmsSchool> {
    return this.json('/api/admin/schools', {}, { method: 'POST', body: JSON.stringify(input) });
  }

  createSchoolSetup(input: SchoolSetupInput): Promise<SchoolSetupResult> {
    return this.json('/api/admin/schools/setup', {}, { method: 'POST', body: JSON.stringify(input) });
  }

  async downloadSchoolCredentials(schoolId: string): Promise<void> {
    const response = await fetch(
      this.url(`/api/admin/schools/${encodeURIComponent(schoolId)}/credentials.csv`),
      { credentials: 'include', headers: { accept: 'text/csv' } }
    );
    if (!response.ok) throw await this.failure(response);
    const disposition = response.headers.get('content-disposition') ?? '';
    const filename = disposition.match(/filename="([^"]+)"/i)?.[1] ?? 'organization-credentials.csv';
    this.downloadBlob(await response.blob(), filename);
  }

  updateSchool(schoolId: string, input: Partial<Pick<CmsSchool, 'code' | 'name' | 'status'>>): Promise<CmsSchool> {
    return this.json(`/api/admin/schools/${encodeURIComponent(schoolId)}`, {}, { method: 'PATCH', body: JSON.stringify(input) });
  }

  addDomain(schoolId: string, domain: string): Promise<CmsDomain> {
    return this.json(`/api/admin/schools/${encodeURIComponent(schoolId)}/domains`, {}, { method: 'POST', body: JSON.stringify({ domain }) });
  }

  updateDomain(schoolId: string, domainId: string, status: CmsStatus): Promise<CmsDomain> {
    return this.json(`/api/admin/schools/${encodeURIComponent(schoolId)}/domains/${encodeURIComponent(domainId)}`, {}, { method: 'PATCH', body: JSON.stringify({ status }) });
  }

  async systemAdmins(query = ''): Promise<readonly CmsSystemAdmin[]> {
    return (await this.json<SystemAdminListResponse>('/api/admin/system-admins', { query })).admins;
  }

  createSystemAdmin(input: { email: string; displayName: string; loginMethod?: CmsLoginMethod; password?: string; status?: CmsStatus }): Promise<CmsSystemAdmin & { temporaryPassword?: string }> {
    return this.json('/api/admin/system-admins', {}, { method: 'POST', body: JSON.stringify(input) });
  }

  updateSystemAdmin(adminId: string, input: Partial<Pick<CmsSystemAdmin, 'email' | 'displayName' | 'loginMethod' | 'status'>> & { password?: string; resetPassword?: boolean }): Promise<CmsSystemAdmin & { temporaryPassword?: string }> {
    return this.json(`/api/admin/system-admins/${encodeURIComponent(adminId)}`, {}, { method: 'PATCH', body: JSON.stringify(input) });
  }

  async students(query = '', schoolId?: string): Promise<readonly CmsStudent[]> {
    return (await this.json<StudentListResponse>('/api/students', { query, schoolId })).students;
  }

  addStudent(input: Record<string, unknown>, schoolId?: string): Promise<StudentMutationResult> {
    return this.json('/api/students', { schoolId }, { method: 'POST', body: JSON.stringify(input) });
  }

  updateStudent(studentId: string, input: Record<string, unknown>): Promise<StudentMutationResult> {
    return this.json(`/api/students/${encodeURIComponent(studentId)}`, {}, { method: 'PATCH', body: JSON.stringify(input) });
  }

  resetStudentPassword(studentId: string): Promise<{ loginName: string; temporaryPassword: string }> {
    return this.json(`/api/students/${encodeURIComponent(studentId)}/reset-password`, {}, { method: 'POST', body: '{}' });
  }

  async teachers(query = '', schoolId?: string): Promise<readonly CmsTeacher[]> {
    return (await this.json<TeacherListResponse>('/api/admin/teachers', { query, schoolId })).teachers;
  }

  createTeacher(input: Record<string, unknown>): Promise<TeacherMutationResult> {
    return this.json('/api/admin/teachers', {}, { method: 'POST', body: JSON.stringify(input) });
  }

  updateTeacher(teacherId: string, input: Record<string, unknown>): Promise<TeacherMutationResult> {
    return this.json(`/api/admin/teachers/${encodeURIComponent(teacherId)}`, {}, { method: 'PATCH', body: JSON.stringify(input) });
  }

  previewImport(kind: 'students' | 'teachers', csv: string, schoolId?: string): Promise<ImportPreview> {
    const path = kind === 'teachers' ? '/api/admin/teachers/import/preview' : '/api/students/import/preview';
    return this.json(path, { schoolId }, { method: 'POST', body: csv, headers: { 'content-type': 'text/csv; charset=utf-8' } });
  }

  commitImport(kind: 'students' | 'teachers', previewToken: string): Promise<ImportResult> {
    const path = kind === 'teachers' ? '/api/admin/teachers/import' : '/api/students/import';
    return this.json(path, {}, { method: 'POST', body: JSON.stringify({ previewToken }) });
  }

  dashboard(filters: ReportFilters): Promise<DashboardReport> {
    return this.json('/api/reports/dashboard', filters);
  }

  schoolReport(filters: ReportFilters): Promise<SchoolReport> {
    return this.json('/api/reports/school', filters);
  }

  studentReport(studentId: string, filters: ReportFilters): Promise<StudentReport> {
    return this.json(`/api/reports/students/${encodeURIComponent(studentId)}`, filters);
  }

  leaderboards(filters: ReportFilters, metric: 'highest' | 'fastest'): Promise<LeaderboardReport> {
    return this.json('/api/reports/leaderboards', { ...filters, metric });
  }

  async audits(schoolId?: string): Promise<readonly CmsAuditEvent[]> {
    return (await this.json<AuditListResponse>('/api/audit-events', { schoolId })).events;
  }

  async downloadReport(filters: ReportFilters, view: 'school' | 'attempts' | 'leaderboard' | 'student', metric: 'highest' | 'fastest' = 'highest'): Promise<void> {
    const response = await fetch(this.url('/api/reports/export.csv', { ...filters, view, metric }), {
      credentials: 'include',
      headers: { accept: 'text/csv' },
    });
    if (!response.ok) throw await this.failure(response);
    this.downloadBlob(await response.blob(), `academy-${view}.csv`);
  }

  downloadCsv(csv: string, filename: string): void {
    this.downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), filename);
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }

  private async json<T>(path: string, query: object = {}, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set('accept', 'application/json');
    if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json');
    const response = await fetch(this.url(path, query), { ...init, credentials: 'include', headers });
    if (!response.ok) throw await this.failure(response);
    return (await response.json()) as T;
  }

  private url(path: string, query: object = {}): URL {
    const base = this.baseUrl ? `${this.baseUrl}/` : window.location.origin;
    const url = new URL(path.replace(/^\//, ''), base);
    for (const [key, rawValue] of Object.entries(query as Record<string, unknown>)) {
      if (rawValue !== undefined && rawValue !== null && rawValue !== '') url.searchParams.set(key, String(rawValue));
    }
    return url;
  }

  private async failure(response: Response): Promise<CmsClientError> {
    let payload: ApiFailure = {};
    try {
      payload = (await response.json()) as ApiFailure;
    } catch {
      // The HTTP status still gives us a useful fallback.
    }
    return new CmsClientError(payload.error ?? `http_${response.status}`, payload.message ?? 'The CMS request could not be completed.', payload.details);
  }
}
